import { memo, useMemo } from 'react';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { useIsAdmin } from '../../../../auth/useIsAdmin';
import { tagTooltipText } from '../../api/dataIssues';
import type { TraderSearchResult } from '../../api/traderSearch';
import { TagChip, type TagState, type TraderLabelStyle } from './TagChip';
import {
    groupDividerSx,
    groupStarts,
    type TraderColumn,
} from './traderColumns';

// The row's py 0.75 is 6 px: the divider reaches over it.
const ROW_DIVIDER_SX = groupDividerSx(6);

// One trader in the event row's design (spec 6.10): the full address, then
// the main category and the labels as chips that toggle the URL filters.
// The trader cell stays pinned when the table scrolls sideways (columns
// spec 4.5).
export interface TraderRowProps {
    trader: TraderSearchResult;
    nowMs: number;
    labels: ReadonlyMap<string, TraderLabelStyle>;
    tagState: (tag: string) => TagState;
    categoryState: (category: string) => TagState;
    onIncludeTag: (tag: string) => void;
    onExcludeTag: (tag: string) => void;
    onIncludeCategory: (category: string) => void;
    onExcludeCategory: (category: string) => void;
    // The visible columns and their grid (useTraderColumns); stable
    // references, so the memoised row only re-renders when the set changes.
    columns: TraderColumn[];
    gridTemplate: string;
}

// Memoised: a page holds up to 200 rows, and the parent renders every second
// while a search runs. It needs stable props (the callbacks take the value as
// an argument, so the parent builds each of them once).
export const TraderRow = memo(function TraderRow({
    trader,
    nowMs,
    labels,
    tagState,
    categoryState,
    onIncludeTag,
    onExcludeTag,
    onIncludeCategory,
    onExcludeCategory,
    columns,
    gridTemplate,
}: TraderRowProps) {
    const theme = useTheme();
    const tint = alpha(theme.palette.primary.main, 0.04);
    const starts = useMemo(() => groupStarts(columns), [columns]);
    const href = `/markets/trader/${trader.address}`;
    const tags = trader.tags ?? [];
    const category = trader.main_category;
    const isAdmin = useIsAdmin();

    return (
        <Box
            data-testid={`trader-row-${trader.address}`}
            sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                columnGap: 1,
                alignItems: 'center',
                px: 1.25,
                py: 0.75,
                borderBottom: 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: tint },
                // The pinned cell is opaque; it takes the same tint over its
                // own background so it does not turn see-through.
                '&:hover .trader-cell': {
                    backgroundImage: `linear-gradient(${tint}, ${tint})`,
                },
            }}
        >
            <Box
                className="trader-cell"
                sx={{
                    minWidth: 0,
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    alignSelf: 'stretch',
                    // Cover the row's padding, so the cell does not move when
                    // it starts to stick and nothing shows around it.
                    ml: -1.25,
                    pl: 1.25,
                    my: -0.75,
                    py: 0.75,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    boxShadow: `1px 0 0 ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Link
                        component={RouterLink}
                        to={href}
                        underline="hover"
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            lineHeight: 1.3,
                            whiteSpace: 'nowrap',
                            color: 'text.primary',
                        }}
                    >
                        {trader.address}
                    </Link>
                    <Tooltip title="Open in a new tab" disableInteractive>
                        <IconButton
                            size="small"
                            component="a"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open the trader page in a new tab"
                            sx={{ p: 0.2 }}
                        >
                            <OpenInNewIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {(category || tags.length > 0) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.4,
                            flexWrap: 'wrap',
                        }}
                    >
                        {category && (
                            <TagChip
                                label={category}
                                strong
                                state={categoryState(category)}
                                onInclude={onIncludeCategory}
                                onExclude={onExcludeCategory}
                            />
                        )}
                        {tags.map(tag => (
                            <TagChip
                                key={tag}
                                label={tag}
                                color={labels.get(tag)?.color}
                                textColor={labels.get(tag)?.textColor}
                                description={tagTooltipText(
                                    tag,
                                    labels.get(tag)?.description,
                                    trader.data_issues,
                                    isAdmin
                                )}
                                state={tagState(tag)}
                                onInclude={onIncludeTag}
                                onExclude={onExcludeTag}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {columns.map(column => (
                // Stretched to the row's height, so the group divider runs
                // through; the inner span keeps the ellipsis.
                <Typography
                    key={column.key}
                    sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        textAlign: 'left',
                        alignSelf: 'stretch',
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        color: column.color?.(trader) ?? 'text.primary',
                        ...(starts.has(column.key) ? ROW_DIVIDER_SX : {}),
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {column.value(trader, nowMs)}
                    </Box>
                </Typography>
            ))}
        </Box>
    );
});
